import { Close as CloseIcon } from '@mui/icons-material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useSendInviteMutation } from '../../../app/Slices/SevenDayApiSlice';
import { openInviteSliceAction } from '../../../app/Slices/inviteSlice';
import toast from '../../shared/Toast';

export type InviteModalProps = {
    open: boolean;
    onClose: () => void;
};

export default function InviteModal({ onClose, open }: InviteModalProps) {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const urlRef = window.location.href.split('/')[5];
    const [sendInvite] = useSendInviteMutation();

    const sapphire = theme.palette.custom.sapphire;

    const headerIcon = (
        <Box
            sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: alpha(sapphire, 0.16),
                color: darken(sapphire, 0.22),
            }}
        >
            <MailOutlineIcon sx={{ fontSize: 26 }} />
        </Box>
    );

    const handleClose = () => {
        onClose();
        dispatch(
            openInviteSliceAction({
                view: 'inviteAborted',
            }),
        );
    };

    const handleSendInvite = () => {
        onClose();
        dispatch(
            openInviteSliceAction({
                view: 'inviteSent',
            }),
        );
        const requestBody = {
            id: parseInt(String(urlRef), 10),
        };
        sendInvite(requestBody)
            .unwrap()
            .then(() => {
                toast.success('Invite link has been sent to user');
            })
            .catch(() => {
                toast.error(
                    'Failed to send invite link, please check email is correct',
                );
            });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth='sm'
            scroll='paper'
            PaperProps={{
                elevation: 0,
                sx: {
                    borderRadius: isMobile ? '20px 20px 0 0' : 3,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    backgroundImage: 'none',
                    ...(isMobile && {
                        m: 0,
                        maxWidth: '100%',
                        width: '100%',
                        position: 'fixed',
                        bottom: 0,
                        maxHeight: 'min(92vh, 560px)',
                    }),
                },
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: isMobile ? 'flex-end' : 'center',
                },
            }}
        >
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    pt: isMobile ? 1 : 3,
                    pb: isMobile
                        ? `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`
                        : 3,
                }}
            >
                {isMobile ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 1.5,
                        }}
                        aria-hidden
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: 'action.disabledBackground',
                            }}
                        />
                    </Box>
                ) : null}

                <Stack
                    direction='row'
                    alignItems='flex-start'
                    justifyContent='space-between'
                    spacing={2}
                    sx={{ mb: 2.5 }}
                >
                    <Stack
                        direction='row'
                        spacing={2}
                        alignItems='flex-start'
                        flex={1}
                        minWidth={0}
                    >
                        {headerIcon}
                        <Box minWidth={0}>
                            <Typography
                                variant='h6'
                                fontWeight={700}
                                color='text.primary'
                            >
                                Send Access Invite
                            </Typography>
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ mt: 0.5 }}
                            >
                                This new user will need to be invited to access
                                7DAY. By clicking &apos;Send Invite&apos; the
                                user will receive an invite link to the email
                                you provided.
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton
                        aria-label='Close'
                        onClick={handleClose}
                        size='small'
                        sx={{
                            display: { xs: 'none', sm: 'inline-flex' },
                            mt: -0.5,
                            color: 'text.secondary',
                            '&:hover': {
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Stack>

                <Stack
                    direction='row'
                    spacing={1.5}
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{
                        pt: 2.5,
                        mt: 1,
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button
                        type='button'
                        variant='outlined'
                        onClick={handleClose}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'divider',
                            color: 'text.primary',
                            px: 2,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type='button'
                        variant='contained'
                        onClick={handleSendInvite}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2.5,
                            boxShadow: 'none',
                            bgcolor: theme.palette.dark.main,
                            '&:hover': {
                                boxShadow: 'none',
                                bgcolor: alpha(theme.palette.dark.main, 0.88),
                            },
                        }}
                    >
                        Send Invite
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
}
